// M13.2 — Depth section on /app/report. Sits directly after
// SourceMixSection, before ExecutiveSummary in the dashboard scroll.
//
// Cohort = the social channel group filter applied to the rest of the
// report (Organic Social + Paid Social), so the numbers reconcile
// row-for-row against TimeSeriesSection's session totals.
//
// Composition (top to bottom):
//   1. Section header — eyebrow + accent bar + title.
//   2. Dual-axis line chart — pageviews (left axis, filled blurple)
//      vs active users (right axis, dashed purple). The visual gap
//      between the two lines tells the "is the audience deep or wide?"
//      story at a glance.
//   3. Per-date datatable: Date / Pageviews / Active users / Total
//      users / Pages per session.

import { useMemo, useState } from "react";
import { rollup } from "d3-array";
import type { ReportPayload } from "../../lib/api";
import { formatTableRowDate } from "../../../shared/report/date-range";
import { SectionHeader } from "./SectionHeader";
import { ScrollX } from "../ui/scroll-x";
import { DepthChart } from "./DepthChart";
import { firstIsoDate, makeDateBucketizer, type Granularity } from "./granularity";

// SKE-3424 — paginate the per-date depth table once it grows past
// 10 rows so a 90d window doesn't render 90 rows in a single scroll.
// Mirrors the LandingPageTable / per-channel-table cadence used
// elsewhere on the report.
type DepthPageSize = 10 | 25 | 50 | "all";
const DEPTH_PAGE_SIZES: DepthPageSize[] = [10, 25, 50, "all"];
const DEPTH_DEFAULT_PAGE_SIZE: DepthPageSize = 10;

export interface DepthSectionProps {
  trend: ReportPayload["trend"];
  granularity: Granularity;
}

export function DepthSection({ trend, granularity }: DepthSectionProps) {
  // Roll up per-(date, platform) trend rows to per-date depth points,
  // honouring the page-level granularity (day / week / month). The
  // shared bucketizer matches TimeSeriesSection's so users see the
  // same buckets across both charts.
  const points = useMemo(() => buildDepthPoints(trend, granularity), [trend, granularity]);

  return (
    <section id="depth" className="scroll-mt-24 space-y-4">
      <SectionHeader
        title="How deeply social visitors explore"
        description={
          <>
            Pageviews sit on the left axis and active users on the right. The gap shows whether
            content consumption is keeping up with the audience reach driving social to your site.
          </>
        }
      />

      <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
        <DepthChart
          rows={points}
          granularity={granularity}
          ariaLabel="Depth chart of pageviews and active users for the social cohort"
        />
      </div>

      <DepthDataTable rows={points} />
    </section>
  );
}

interface DepthDataTableProps {
  rows: DepthRow[];
}

function DepthDataTable({ rows }: DepthDataTableProps) {
  const [pageSize, setPageSize] = useState<DepthPageSize>(DEPTH_DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);

  const totalRows = rows.length;
  const effectivePageSize = pageSize === "all" ? totalRows || 1 : pageSize;
  const pageCount = Math.max(1, Math.ceil(totalRows / effectivePageSize));
  const safePage = Math.min(currentPage, pageCount - 1);
  const sliceStart = safePage * effectivePageSize;
  const sliceEnd = sliceStart + effectivePageSize;
  const pagedRows = useMemo(() => rows.slice(sliceStart, sliceEnd), [rows, sliceStart, sliceEnd]);
  const showPagination = totalRows > 10;
  const visibleFrom = totalRows === 0 ? 0 : sliceStart + 1;
  const visibleTo = Math.min(sliceEnd, totalRows);

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-5 text-sm text-neutral-500 ring-1 ring-neutral-950/5">
        No depth data for this window.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <ScrollX className="rounded-2xl bg-white ring-1 ring-neutral-950/5">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left">
              <th className="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Date
              </th>
              <th className="whitespace-nowrap px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Pageviews
              </th>
              <th className="whitespace-nowrap px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Active users
              </th>
              <th className="whitespace-nowrap px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Total users
              </th>
              <th className="whitespace-nowrap px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Pages / session
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((r) => {
              const pps = r.sessions > 0 ? r.pageviews / r.sessions : 0;
              return (
                <tr key={r.date} className="border-b border-neutral-100 last:border-b-0">
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium text-neutral-950">
                    {formatTableDate(r.date)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-950">
                    {formatNumber(r.pageviews)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                    {formatNumber(r.activeUsers)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                    {formatNumber(r.totalUsers)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                    {pps.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollX>
      {showPagination ? (
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-600">
          <label className="flex items-center gap-2">
            <span className="text-neutral-500">Rows per page</span>
            <select
              value={pageSize === "all" ? "all" : String(pageSize)}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === "all" ? "all" : (Number(v) as DepthPageSize));
                setCurrentPage(0);
              }}
              className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
            >
              {DEPTH_PAGE_SIZES.map((size) => (
                <option key={String(size)} value={String(size)}>
                  {size === "all" ? "All" : size}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-3">
            <span className="tabular-nums text-neutral-500">
              {visibleFrom.toLocaleString()} to {visibleTo.toLocaleString()} of{" "}
              {totalRows.toLocaleString()}
            </span>
            <nav className="flex items-center gap-1" aria-label="Depth pagination">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
                disabled={safePage === 0}
                aria-label="Previous page"
                className="inline-flex size-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-200 disabled:hover:text-neutral-600 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                <i className="fa-regular fa-chevron-left text-xs" aria-hidden="true" />
              </button>
              <span className="tabular-nums px-2 text-neutral-700 max-sm:hidden">
                Page {safePage + 1} of {pageCount}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(pageCount - 1, safePage + 1))}
                disabled={safePage >= pageCount - 1}
                aria-label="Next page"
                className="inline-flex size-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-200 disabled:hover:text-neutral-600 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                <i className="fa-regular fa-chevron-right text-xs" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// --- helpers --------------------------------------------------------------

interface DepthRow {
  date: string;
  pageviews: number;
  activeUsers: number;
  totalUsers: number;
  sessions: number;
}

function buildDepthPoints(trend: ReportPayload["trend"], granularity: Granularity): DepthRow[] {
  if (trend.length === 0) return [];
  const bucketize = makeDateBucketizer(granularity, firstIsoDate(trend));

  // Sum every metric per date bucket so the chart + table reflect the
  // social cohort's totals (not per-platform splits).
  const grouped = rollup(
    trend,
    (rows) => {
      let pageviews = 0;
      let activeUsers = 0;
      let totalUsers = 0;
      let sessions = 0;
      for (const r of rows) {
        pageviews += r.pageviews;
        activeUsers += r.activeUsers;
        totalUsers += r.totalUsers;
        sessions += r.sessions;
      }
      return { pageviews, activeUsers, totalUsers, sessions };
    },
    (r) => bucketize(r.date),
  );

  return Array.from(grouped.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([date, vals]) => ({
      date,
      pageviews: vals.pageviews,
      activeUsers: vals.activeUsers,
      totalUsers: vals.totalUsers,
      sessions: vals.sessions,
    }));
}

function formatTableDate(iso: string): string {
  try {
    return formatTableRowDate(iso);
  } catch {
    return iso;
  }
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat("en").format(Math.round(n));
}
